import { Chain } from "@rainbow-me/rainbowkit";
import { mainnet } from "viem/chains";
import { pick } from "lodash-es";
import { registerInDevtools, Store } from "pullstate";
import { TransactionReceipt } from "viem";
import { initialWrapQuote } from "../../pages/Wrap/elements/WrapEntry";
import { Theme, themes } from "../../styles/theme";
import { Amount } from "../../types/amount";
import { Order, OrderError } from "../../types/order";
import { OrderStatus } from "../../types/orderStatus";
import { Quote, QuoteError } from "../../types/quote";
import { SignedMessage } from "../../types/signedMessage";
import { WrapQuote, WrapStatus } from "../../types/wrapQuote";
import { getWrappedSymbolForChain } from "../../utils/chainUtils";
import { PermitType } from "../../utils/permitUtils";
import { INITIAL_RECEIVER_STATE } from "../../utils/swapAndSendUtils";
import { initURLParams } from "../../utils/urlUtils";
import { ActivityStore } from "../activity/ActivityStore";
import { Allowances } from "./getAllowances";
import { Balances } from "./getBalances";
import { Role } from "./getGuildRoles";
import { TokenMetadata } from "./getTokenMetadata";
import { attachReactions } from "./reactions";
import { Token } from "./TokenEnum";

export type SelectedToken = MakerToken | TakerToken;

export type MakerToken = TakerToken & WithAllocationPct;

export interface TakerToken {
  token: Token;
  amount: Amount;
}

export interface WithAllocationPct {
  pct: number;
  isLocked: boolean;
}

export interface Receiver {
  ensName: string;
  address: string;
  avatar: string;
  userInput: string;
  isAddressLoading: boolean;
}

export interface IGlobalStore {
  address: string;
  allowances: Allowances;
  autoRefreshQuote: boolean;
  balances: Balances;
  ensName: string;
  ensAvatar: string;
  zerionNft: string;
  theme: Theme;
  isSending: boolean;
  receiver: Receiver;
  isRequestingQuote: boolean;
  isMovingSlider: boolean;
  makerTokens: Array<SelectedToken & WithAllocationPct>;
  modalSelector: string | undefined;
  network: Chain;
  order: Order | undefined;
  orderError: OrderError | undefined;
  orderStatus: OrderStatus;
  quote: Quote | undefined;
  quoteError: QuoteError | undefined;
  receiverError: string;
  quoteExpired: boolean;
  signedMessage: SignedMessage | undefined;
  takerTokens: Array<SelectedToken>;
  tokenList: Array<Token>;
  tokens: TokenMetadata | undefined;
  userRoles: Array<Role>;
  wrapQuote: WrapQuote | undefined;
  wrapStatus: WrapStatus;
  permitType: PermitType | undefined;
  permits: {
    permit1: {
      signature: `0x${string}` | undefined;
      deadline: number | undefined;
    };
    permit2: {
      signature: `0x${string}` | undefined;
      deadline: number | undefined;
      nonces: Array<{ token: `0x${string}`; nonce: number }>;
      txData: TransactionReceipt | undefined;
    };
  };
}

export const INITIAL_STATE = {
  address: "",
  allowances: [],
  autoRefreshQuote: true,
  balances: [],
  ensName: "",
  ensAvatar: "",
  theme: themes.dark,
  isRequestingQuote: false,
  isMovingSlider: false,
  isSending: false,
  makerTokens: [],
  modalSelector: undefined,
  network: mainnet,
  receiver: INITIAL_RECEIVER_STATE,
  receiverError: "",
  order: undefined,
  orderError: undefined,
  orderStatus: OrderStatus.ENTRY,
  quote: undefined,
  quoteError: undefined,
  quoteExpired: false,
  signedMessage: undefined,
  takerTokens: [
    {
      token: getWrappedSymbolForChain(mainnet),
      amount: Amount.ZERO,
    },
  ],
  tokenList: [],
  tokens: undefined,
  userRoles: [],
  wrapQuote: initialWrapQuote,
  wrapStatus: WrapStatus.ENTRY,
  zerionNft: "",
  permitType: undefined,
  permits: {
    permit1: {
      signature: undefined,
      deadline: undefined,
    },
    permit2: {
      signature: undefined,
      deadline: undefined,
      nonces: [],
      txData: undefined,
    },
  },
};
if (initURLParams) {
  // Needs to be wrapped in an if statement because of jest initialisation order
  initURLParams();
}
export const GlobalStore = new Store<IGlobalStore>(INITIAL_STATE);
/**
 * resetOrderState is used to reset the UI to an initial "Order Entry" state.
 * We reset everything other than the current network, connected wallet and token lists, balances and allowances
 */
export const resetOrderState = (): void => {
  GlobalStore.update(s => {
    const stateToClear = pick(INITIAL_STATE, [
      "autoRefreshQuote",
      "isSending",
      "receiver",
      "isRequestingQuote",
      "isMovingSlider",
      "makerTokens",
      "takerTokens",
      "order",
      "orderError",
      "orderStatus",
      "quote",
      "quoteError",
      "receiverError",
      "quoteExpired",
      "signedMessage",
      "wrapQuote",
      "wrapStatus",
    ]);

    return {
      ...s,
      ...stateToClear,
      takerTokens: [
        {
          token: getWrappedSymbolForChain(s.network),
          amount: Amount.ZERO,
        },
      ],
    };
  });
};
export const resetWrapState = () => {
  GlobalStore.update(s => {
    const stateToClear = pick(INITIAL_STATE, ["wrapQuote", "wrapStatus"]);

    return {
      ...s,
      ...stateToClear,
    };
  });
};

export const resetUserState = () => {
  const stateToClear = pick(INITIAL_STATE, [
    "address",
    "allowances",
    "balances",
    "ensName",
    "ensAvatar",
    "modalSelector",
    "receiver",
    "zerionNft",
    "userRoles",
  ]);

  GlobalStore.update(s => {
    return {
      ...s,
      ...stateToClear,
    };
  });
};

// Register GlobalStore with Redux Devtools
registerInDevtools({
  GlobalStore: GlobalStore,
  ActivityStore,
});

attachReactions();
